import type { CalendarEvent } from '../types';
import { EventCard } from './EventCard';
import './EventList.css';

interface EventListProps {
  events: CalendarEvent[];
  hasMore?: boolean;
  onLoadMore?: () => void;
  loadingMore?: boolean;
  hasActiveFilters?: boolean;
  onClearFilters?: () => void;
  onOpenDetail?: (event: CalendarEvent) => void;
  isSaved?: (id: string) => boolean;
  emptyTitle?: string;
  emptyDesc?: string;
}

export function EventList({
  events,
  hasMore,
  onLoadMore,
  loadingMore,
  hasActiveFilters,
  onClearFilters,
  onOpenDetail,
  isSaved,
  emptyTitle = 'No events match your filters',
  emptyDesc = 'Try a different date range or category, or clear filters to see all events.',
}: EventListProps) {
  if (events.length === 0) {
    return (
      <div className="event-list-empty" role="status">
        <div className="event-list-empty-icon" aria-hidden="true">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
        </div>
        <p className="event-list-empty-title">{emptyTitle}</p>
        <p className="event-list-empty-desc">{emptyDesc}</p>
        {hasActiveFilters && onClearFilters && (
          <button type="button" className="event-list-empty-btn" onClick={onClearFilters}>
            Clear filters
          </button>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="calendar-results-header">
        <p className="calendar-results-count">
          <strong>{events.length}</strong> {events.length === 1 ? 'event' : 'events'}
        </p>
      </div>
      <ul className="event-list" aria-label="Events">
        {events.map((event) => (
          <li key={event.id}>
            <EventCard
              event={event}
              isSaved={isSaved?.(event.id)}
              onOpenDetail={onOpenDetail ? () => onOpenDetail(event) : undefined}
            />
          </li>
        ))}
      </ul>
      {hasMore && onLoadMore && (
        <div className="event-list-load-more">
          <button
            type="button"
            className="event-list-load-more-btn"
            onClick={onLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading…' : 'Load more events'}
          </button>
        </div>
      )}
    </>
  );
}
